import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { mediaProxyUrl } from 'js/src/libs/media-proxy';
import styles from 'css/src/components/agent/BrandKitResults.module.scss';

import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { z } from 'zod';

import { BrandKitRegistry } from '../../registries/BrandKitRegistry';
import type {
    CustomRendererComponentProps,
    EnhancedToolResult,
    ToolExecutionContext,
} from '../../types';
import { CustomRendererMode } from '../../types';
import { Tool } from '../Tool';

import { colord } from 'colord';
import type { AssetContainerModel } from 'js/src/client';
import { ApiService, AssetContainerTypes, SourceEnum } from 'js/src/client';
import type { BrandKitColorRequest, BrandKitRequest, FontAsset } from 'js/src/client/types.gen';
import { CategoryEnum } from 'js/src/client/types.gen';
import Button, { SPINNER_ICON } from 'js/src/components/common/ui/Button';
import Tooltip from 'js/src/components/common/ui/Tooltip';
import UpsellIcon, { ICON_SIZES } from 'js/src/components/common/ui/UpsellIcon';
import { getIsUserAllowedAccessToAccessBlueprintsAndBrandKits } from 'js/src/data/account/account-selectors';
import {
    loadBrandKits,
    loadTeamBrandKitsCount,
} from 'js/src/data/brand-kits/brand-kits-operations';
import { getTeamBrandKitsCount } from 'js/src/data/brand-kits/brand-kits-selectors';
import { LOCKED_FEATURES, MODAL_IDS, showModal } from 'js/src/data/modals';
import type { FileWithMetadata } from 'js/src/data/uploads/uploads-types';
import { type DeepReadonly, PLAN_PREFIX } from 'js/src/libs/consts';
import { useAppDispatch } from 'js/src/libs/RootState';
import UploadMediaController from 'js/src/libs/upload-media-controller';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';

export const saveBrandKitSchema = z.object({
    name: z.string().describe('The brand name.'),
    brandColors: z
        .array(z.string())
        .describe('Array of 3-5 hex color strings (e.g. ["#e85d26", "#1a3c5e"]).'),
    darkAnchor: z.string().describe('Hex color for the dark anchor (near-black text color).'),
    lightAnchor: z
        .string()
        .describe('Hex color for the light anchor (near-white background color).'),
    headingFont: z
        .string()
        .nullable()
        .describe('Font family name for headings, or null if unknown.'),
    bodyFont: z.string().nullable().describe('Font family name for body text, or null if unknown.'),
    logoIndex: z
        .number()
        .int()
        .nullable()
        .describe(
            'Index of the chosen logo from the extraction results, or null if no suitable logo.',
        ),
    extractionId: z
        .string()
        .describe(
            'The extractionId from the extraction result that contains the chosen logo. Required when logoIndex is set and multiple extractions were performed.',
        )
        .optional(),
});
export type SaveBrandKitArgs = z.infer<typeof saveBrandKitSchema>;

interface FontMatch {
    name: string;
    id: number | null;
    source: 'matched' | 'uploaded' | 'not_found';
}

interface ResolvedLogo {
    src: string;
    source: string;
}

export interface SaveBrandKitResult {
    name: string;
    brandColors: string[];
    darkAnchor: string;
    lightAnchor: string;
    headingFont: FontMatch | null;
    bodyFont: FontMatch | null;
    logo: ResolvedLogo | null;
    brandKitRequest: BrandKitRequest;
}

function hexToColorRequest(hex: string, category: CategoryEnum): BrandKitColorRequest {
    const { r, g, b } = colord(hex).toRgb();
    return { r, g, b, a: 100, category };
}

function levenshtein(a: string, b: string): number {
    const m = a.length;
    const n = b.length;
    const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;
    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            dp[i][j] =
                a[i - 1] === b[j - 1]
                    ? dp[i - 1][j - 1]
                    : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
        }
    }
    return dp[m][n];
}

function fontSimilarity(query: string, candidate: string): number {
    const q = query.toLowerCase().trim();
    const c = candidate.toLowerCase().trim();
    if (q === c) return 1;
    if (c.includes(q) || q.includes(c)) return 0.9;
    const maxLen = Math.max(q.length, c.length);
    if (maxLen === 0) return 0;
    return 1 - levenshtein(q, c) / maxLen;
}

const MIN_FONT_SIMILARITY = 0.4;

function imageUrlToFile(url: string, fileName: string): Promise<File | null> {
    return new Promise((resolve) => {
        const img = new Image();
        if (!url.startsWith('data:')) {
            img.crossOrigin = 'anonymous';
        }
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d')!;
            ctx.drawImage(img, 0, 0);
            canvas.toBlob((blob) => {
                if (!blob) {
                    resolve(null);
                    return;
                }
                resolve(new File([blob], fileName, { type: 'image/png' }));
            }, 'image/png');
        };
        img.onerror = () => resolve(null);
        img.src = url;
    });
}

function uploadLogoAsWatermark(
    logoUrl: string,
    brandName: string,
    sourceId: string,
): Promise<string | null> {
    return new Promise(async (resolve) => {
        const file = await imageUrlToFile(mediaProxyUrl(logoUrl), `${brandName}-logo.png`);
        if (!file) {
            resolve(null);
            return;
        }

        const uploader = new UploadMediaController({
            file_list: [{ file }],
            only_images: true,
            asset_type: AssetContainerTypes.BACKGROUND,
            metadata: {
                source: SourceEnum.SOURCE_USER_WATERMARK,
                source_id: sourceId,
            },
            upload_succeeded_callback: (
                _file: FileWithMetadata,
                newAsset: DeepReadonly<AssetContainerModel>,
            ) => {
                const uuid = newAsset.background?.image?.uuid;
                resolve(uuid ?? null);
            },
            upload_failed_callback: () => {
                resolve(null);
            },
        });
        uploader.upload();
    });
}

function fuzzyMatchFont(fontName: string, fonts: FontAsset[]): FontAsset | null {
    let bestFont: FontAsset | null = null;
    let bestScore = 0;
    for (const font of fonts) {
        const score = fontSimilarity(fontName, font.name);
        if (score > bestScore) {
            bestScore = score;
            bestFont = font;
        }
    }
    return bestScore >= MIN_FONT_SIMILARITY ? bestFont : null;
}

function uploadFontFile(
    dataUrl: string,
    family: string,
    format: 'ttf' | 'otf',
): Promise<number | null> {
    return new Promise((resolve) => {
        const byteString = atob(dataUrl.split(',')[1]);
        const mime = dataUrl.split(',')[0].split(':')[1].split(';')[0];
        const bytes = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
            bytes[i] = byteString.charCodeAt(i);
        }
        const file = new File([bytes], `${family.replace(/\s+/g, '-')}.${format}`, { type: mime });

        const uploader = new UploadMediaController({
            file_list: [{ file }],
            asset_type: AssetContainerTypes.FONT,
            upload_succeeded_callback: (
                _file: FileWithMetadata,
                newAsset: DeepReadonly<AssetContainerModel>,
            ) => {
                resolve(newAsset.font?.id ?? null);
            },
            upload_failed_callback: () => resolve(null),
        });
        uploader.upload();
    });
}

export class SaveBrandKitTool extends Tool<typeof saveBrandKitSchema> {
    protected override readonly schema = saveBrandKitSchema;
    protected override readonly toolName = TOOL_NAMES.saveBrandKit;
    public override readonly subagentAccess: SubagentAccess = SubagentAccess.UNAVAILABLE;
    protected override readonly toolDescription: string =
        'Save a curated brand kit with specific colors, fonts, and logo. Call this after analyzing the raw brand data to present the user with a saveable brand kit.';

    public override customRendererMode = CustomRendererMode.INLINE;
    public override customRenderer = SaveBrandKitRenderer;

    public override getToolDisplayName(): string {
        return 'Synthesizing brand kit';
    }

    /**
     * The brand kit is only actually persisted when the user clicks "Save" in the
     * renderer, which runs after this turn ends — so the agent can't react to the
     * backend's "limit reached" 400. Check the limit up front instead and, when the
     * user can't save, return an error that tells the agent to surface an upgrade
     * prompt rather than rendering a Save button that will fail.
     */
    private async checkBrandKitLimit(): Promise<EnhancedToolResult | null> {
        if (!getIsUserAllowedAccessToAccessBlueprintsAndBrandKits(this.getState())) {
            return {
                result: `Error: This plan does not include brand kits. Use the ${TOOL_NAMES.suggestUpgrade} tool to show the user that they need to upgrade.`,
                isSuccess: false,
            };
        }

        if (getTeamBrandKitsCount(this.getState()) === null) {
            await this.dispatch(loadTeamBrandKitsCount());
        }
        const count = getTeamBrandKitsCount(this.getState());
        const max = SERVER?.USER?.capabilities?.max_brand_kits ?? 0;
        if (count !== null && count >= max) {
            return {
                result: `Error: The user has reached their brand kit limit (${count}/${max}) and cannot save another. Use the ${TOOL_NAMES.suggestUpgrade} tool to show the user that they need to upgrade.`,
                isSuccess: false,
            };
        }

        return null;
    }

    public get statusIcon(): string {
        return 'palette';
    }

    public async execute(
        args: SaveBrandKitArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const {
            name,
            brandColors,
            darkAnchor,
            lightAnchor,
            headingFont,
            bodyFont,
            logoIndex,
            extractionId: explicitExtractionId,
        } = args;

        const upgradeError = await this.checkBrandKitLimit();
        if (upgradeError) {
            return upgradeError;
        }

        let fonts: FontAsset[] = [];
        try {
            const response = await ApiService.fontAssetsList();
            fonts = response.data;
        } catch {
            // Font matching will just return no matches
        }

        // Fuzzy match against the font library first
        const headingFuzzy = headingFont ? fuzzyMatchFont(headingFont, fonts) : null;
        const bodyFuzzy = bodyFont ? fuzzyMatchFont(bodyFont, fonts) : null;

        // Search all extractions for font file data (fonts may come from different URLs)
        const registry = BrandKitRegistry.getInstance();
        const extractionId = explicitExtractionId ?? registry.getLatestExtractionId();
        const extraction = extractionId ? registry.getExtraction(extractionId) : null;
        const canUploadFonts =
            typeof SERVER !== 'undefined' && SERVER?.USER?.capabilities?.custom_font;

        // Upload unmatched fonts if font file data is available
        const uploadedFontIds: Record<string, number | null> = {};

        if (canUploadFonts) {
            const fontsToUpload: Array<{ family: string; dataUrl: string; format: 'ttf' | 'otf' }> =
                [];

            if (headingFont && !headingFuzzy) {
                const fontFile = registry.findFontFile(headingFont);
                if (fontFile) {
                    fontsToUpload.push({
                        family: headingFont,
                        dataUrl: fontFile.dataUrl,
                        format: fontFile.format,
                    });
                }
            }

            if (bodyFont && !bodyFuzzy && bodyFont !== headingFont) {
                const fontFile = registry.findFontFile(bodyFont);
                if (fontFile) {
                    fontsToUpload.push({
                        family: bodyFont,
                        dataUrl: fontFile.dataUrl,
                        format: fontFile.format,
                    });
                }
            }

            const uploadResults = await Promise.all(
                fontsToUpload.map((f) => uploadFontFile(f.dataUrl, f.family, f.format)),
            );

            fontsToUpload.forEach((f, i) => {
                uploadedFontIds[f.family] = uploadResults[i];
            });
        }

        function resolveFont(
            fontName: string | null,
            fuzzyResult: FontAsset | null,
        ): FontMatch | null {
            if (!fontName) return null;
            if (fuzzyResult) {
                return { name: fontName, id: fuzzyResult.id, source: 'matched' };
            }
            const uploadedId = uploadedFontIds[fontName] ?? null;
            if (uploadedId != null) {
                return { name: fontName, id: uploadedId, source: 'uploaded' };
            }
            return { name: fontName, id: null, source: 'not_found' };
        }

        const headingMatch = resolveFont(headingFont, headingFuzzy);
        // If body is the same font as heading, reuse the same result
        const bodyMatch =
            bodyFont === headingFont ? headingMatch : resolveFont(bodyFont, bodyFuzzy);

        const colors: BrandKitColorRequest[] = [
            ...brandColors.map((hex) => hexToColorRequest(hex, CategoryEnum.BRAND)),
            hexToColorRequest(darkAnchor, CategoryEnum.NEUTRAL),
            hexToColorRequest(lightAnchor, CategoryEnum.NEUTRAL),
        ];

        const fontIds: number[] = [];
        if (headingMatch?.id != null) fontIds.push(headingMatch.id);
        if (bodyMatch?.id != null && bodyMatch.id !== headingMatch?.id) fontIds.push(bodyMatch.id);

        // Look up the logo from the extraction registry (reuse extraction fetched above)
        let logo: ResolvedLogo | null = null;
        let watermark: string | null = null;
        if (logoIndex != null) {
            const logoData = extraction?.logos[logoIndex];
            if (logoData) {
                logo = { src: logoData.src, source: logoData.source };
                const logoSourceId = logoData.src.startsWith('data:')
                    ? (extraction?.url ?? `${name}-logo`)
                    : logoData.src;
                const uuid = await uploadLogoAsWatermark(logoData.src, name, logoSourceId);
                if (uuid) {
                    watermark = uuid;
                }
            }
        }

        const brandKitRequest: BrandKitRequest = {
            name,
            colors,
            fonts: fontIds,
            watermark,
        };

        const resultData: SaveBrandKitResult = {
            name,
            brandColors,
            darkAnchor,
            lightAnchor,
            headingFont: headingMatch,
            bodyFont: bodyMatch,
            logo,
            brandKitRequest,
        };

        return this.buildResult(resultData);
    }

    /**
     * Turn the curated kit into the tool result. The base (Magic-brand-kit smart
     * action) flow stops the conversation so the user can review and click Save in
     * the interactive renderer before the agent moves on. The Toolbox overrides
     * this to persist the kit itself and keep the conversation going.
     */
    protected async buildResult(resultData: SaveBrandKitResult): Promise<EnhancedToolResult> {
        return {
            result: JSON.stringify(resultData),
            stopConversation: true,
            isSuccess: true,
        };
    }
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export const BrandKitPreview: React.FC<{ data: SaveBrandKitResult }> = ({ data }) => {
    const dispatch = useAppDispatch();
    const canUseCustomFont = window.SERVER.USER.capabilities.custom_font;
    const canUseWatermark = window.SERVER.USER.capabilities.custom_watermark;

    const handleFontUpsell = () => {
        dispatch(
            showModal({
                modal_id: MODAL_IDS.FEATURE_LOCK,
                modal_context: { featureName: LOCKED_FEATURES.CUSTOM_FONT },
            }),
        );
    };

    const handleWatermarkUpsell = () => {
        dispatch(
            showModal({
                modal_id: MODAL_IDS.FEATURE_LOCK,
                modal_context: {
                    featureName: LOCKED_FEATURES.CUSTOM_WATERMARK,
                },
            }),
        );
    };

    return (
        <>
            <div className="fw-bold mb-l5-12 color-grey-900">{data.name}</div>

            <div className="mb-l5-12">
                <div className={styles['section-label']}>Brand colors</div>
                <div className={styles['color-row']}>
                    {data.brandColors.map((hex, i) => (
                        <div
                            key={i}
                            className={styles['color-item-swatch']}
                            style={{ backgroundColor: hex }}
                            title={hex}
                        />
                    ))}
                </div>
            </div>

            <div className="mb-l5-12">
                <div className={styles['section-label']}>Neutral colors</div>
                <div className={styles['color-row']}>
                    {[data.darkAnchor, data.lightAnchor].map((hex, i) => (
                        <div
                            key={i}
                            className={styles['color-item-swatch']}
                            style={{ backgroundColor: hex }}
                            title={hex}
                        />
                    ))}
                </div>
            </div>

            {(data.headingFont || data.bodyFont) && (
                <div className="mb-l5-12">
                    <div className={styles['section-label']}>
                        Fonts
                        {!canUseCustomFont && (
                            <Tooltip title="Upgrade to use custom fonts">
                                <UpsellIcon size={ICON_SIZES.XS} onClick={handleFontUpsell} />
                            </Tooltip>
                        )}
                    </div>
                    <div className={styles['font-row']}>
                        {data.headingFont && (
                            <div className="d-flex flex-column">
                                <div className={styles['font-name']}>{data.headingFont.name}</div>
                                <div className={styles['font-role']}>Primary</div>
                            </div>
                        )}
                        {data.bodyFont && data.bodyFont.name !== data.headingFont?.name && (
                            <div className="d-flex flex-column">
                                <div className={styles['font-name']}>{data.bodyFont.name}</div>
                                <div className={styles['font-role']}>Secondary</div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {data.logo && (
                <div>
                    <div className={styles['section-label']}>
                        Watermark
                        {!canUseWatermark && (
                            <Tooltip title="Upgrade to use custom watermarks">
                                <UpsellIcon size={ICON_SIZES.XS} onClick={handleWatermarkUpsell} />
                            </Tooltip>
                        )}
                    </div>
                    <img
                        src={mediaProxyUrl(data.logo.src)}
                        alt={`${data.name} watermark`}
                        className={styles['logo-thumbnail']}
                    />
                </div>
            )}
        </>
    );
};

const SaveBrandKitRenderer: React.FC<CustomRendererComponentProps> = ({ isLoading, result }) => {
    const dispatch = useAppDispatch();
    const teamBrandKitsCount = useSelector(getTeamBrandKitsCount);
    const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    if (isLoading) {
        return null;
    }

    // A non-JSON result is an error string meant for the agent (e.g. the brand
    // kit limit message that tells it to call suggest_upgrade), not something to
    // surface to the user. The tool-call header already shows the error state.
    let data: SaveBrandKitResult;
    try {
        data = JSON.parse(result);
    } catch {
        return null;
    }

    const showBrandKitLimitUpsell = () => {
        const planName = (window.SERVER.USER.plan || '').toLowerCase();
        const isBusinessOrEnterprise =
            planName.includes(PLAN_PREFIX.BUSINESS) || planName.includes(PLAN_PREFIX.ENTERPRISE);
        dispatch(
            showModal({
                modal_id: MODAL_IDS.FEATURE_LOCK,
                modal_context: {
                    featureName: isBusinessOrEnterprise
                        ? LOCKED_FEATURES.BRAND_KITS_LIMIT_CONTACT_US
                        : LOCKED_FEATURES.BRAND_KITS_LIMIT,
                },
            }),
        );
    };

    const handleSave = async () => {
        setSaveStatus('saving');
        setErrorMessage('');
        try {
            await ApiService.brandKitsCreate({ body: data.brandKitRequest });
            dispatch(loadBrandKits());
            setSaveStatus('saved');
        } catch (err: unknown) {
            const maxBrandKits = window.SERVER.USER.capabilities.max_brand_kits;
            if (teamBrandKitsCount !== null && teamBrandKitsCount >= maxBrandKits) {
                showBrandKitLimitUpsell();
                setSaveStatus('idle');
                return;
            }
            const message = err instanceof Error ? err.message : 'Failed to save brand kit';
            setErrorMessage(message);
            setSaveStatus('error');
        }
    };

    return (
        <div className={styles['save-preview']}>
            <BrandKitPreview data={data} />

            <div className="mt-l5-12">
                {saveStatus === 'saved' ? (
                    <div className="d-flex align-items-center fw-bold color-success-600">
                        <span translate="no" className="material-symbols-outlined me-l5-4">
                            check_circle
                        </span>
                        Saved!
                    </div>
                ) : (
                    <>
                        <Button
                            text="Save Brand Kit"
                            onClick={handleSave}
                            disabled={saveStatus === 'saving'}
                            iconLeft={saveStatus === 'saving' ? SPINNER_ICON : undefined}
                        />
                        {saveStatus === 'error' && (
                            <div className={styles['save-error']}>{errorMessage}</div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

const ReadOnlyBrandKitRenderer: React.FC<CustomRendererComponentProps> = ({
    isLoading,
    result,
}) => {
    if (isLoading) {
        return null;
    }

    let data: SaveBrandKitResult;
    try {
        data = JSON.parse(result);
    } catch {
        return null;
    }

    return (
        <div className={styles['save-preview-panel']}>
            <BrandKitPreview data={data} />
        </div>
    );
};

export class PanelSaveBrandKitTool extends SaveBrandKitTool {
    public override readonly subagentAccess = SubagentAccess.UNAVAILABLE;
    public override customRenderer = ReadOnlyBrandKitRenderer;
}

/**
 * Toolbox variant. The agent is mid-conversation building a video, so it saves
 * the kit itself and keeps going, rather than showing an interactive Save button
 * and halting the turn — that button and the `stopConversation` stop belong only
 * to the standalone Magic-brand-kit smart action. The renderer is the read-only
 * preview.
 */
export class ToolboxSaveBrandKitTool extends SaveBrandKitTool {
    public override readonly subagentAccess = SubagentAccess.READ_ONLY;
    // The base description ("present the user with a saveable brand kit") describes
    // the smart action's Save button. Here the call persists the kit immediately
    // with no user step, and this tool is always registered — so the model may
    // reach it without loading the create-brand-kit skill, making this description
    // the only thing telling it what the call does.
    protected override readonly toolDescription = `Save a curated brand kit (colors, fonts, logo) to the workspace and return its brandKitId. This saves immediately with no user confirmation step, so only call it once you are confident in the curated data. Apply it to the video next with ${TOOL_NAMES.applyBrandKit}.`;

    public override customRenderer = ReadOnlyBrandKitRenderer;

    protected override async buildResult(
        resultData: SaveBrandKitResult,
    ): Promise<EnhancedToolResult> {
        try {
            const response = await ApiService.brandKitsCreate({
                body: resultData.brandKitRequest,
            });
            await this.dispatch(loadBrandKits());
            return {
                result: JSON.stringify({ ...resultData, brandKitId: response.data.id }),
                isSuccess: true,
            };
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to save brand kit';
            return {
                result: `Error saving brand kit: ${message}`,
                isSuccess: false,
            };
        }
    }
}
